<template>
  <SideMenuPlayerWidget
    ref="playerRef"
    :poster="props.poster"
    :default-selection="props.defaultSelection"
    :enable-preload="props.enablePreload"
    :language-options="languageOptions"
    :texts="texts"
    :build-default-poster-url="buildDefaultPosterUrl"
    :build-video-url="buildVideoUrl"
    :build-video-poster-url="buildVideoPosterUrl"
    :build-video-title="buildVideoTitle"
    :build-video-description="buildVideoDescription"
    @filter-change="emit('filter-change', $event)"
    @video-play="emit('video-play')"
    @video-pause="emit('video-pause')"
    @video-ended="emit('video-ended')"
    @video-error="emit('video-error', $event)"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import SideMenuPlayerWidget from '~/design/widgets/side-menu-player/index.vue';
import type {
  LanguageOption,
  Selection,
  SelectionLabels,
  SideMenuPlayerTexts,
} from '~/design/widgets/side-menu-player/types';
import { getFeUrl } from '~/utils/url';

const props = withDefaults(
  defineProps<{
    poster?: string;
    defaultSelection?: Selection;
    enablePreload?: boolean;
  }>(),
  {
    poster: '',
    defaultSelection: () => ({
      language: 'chinese',
      gender: 'female',
      style: 'baomanjiang',
    }),
    enablePreload: true,
  },
);

const emit = defineEmits<{
  (e: 'filter-change', filters: Selection): void;
  (e: 'video-play'): void;
  (e: 'video-pause'): void;
  (e: 'video-ended'): void;
  (e: 'video-error', error: Event): void;
}>();

// ==================== 筛选选项配置 ====================
const languageOptions: LanguageOption[] = [
  {
    label: '中文',
    value: 'chinese',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '饱满激昂', value: 'baomanjiang' },
          { label: '积极活力', value: 'jijihuoli' },
          { label: '甜美可爱', value: 'tianmeikeai' },
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '饱满激昂', value: 'baomanjiang' },
          { label: '积极活力', value: 'jijihuoli' },
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '中文方言',
    value: 'chinese-dialect',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '东北妹子', value: 'dongbeimeizi' },
          { label: '南方姑娘', value: 'nanfangguniang' },
          { label: '清新台妹', value: 'qingxintaimei' },
          { label: '湾区靓女', value: 'wanquliangnv' },
          { label: '吴侬软语', value: 'wunongruanyu' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '川渝小哥', value: 'chuanyuxiaoge' },
          { label: '东北老铁', value: 'dongbeilaotie' },
          { label: '广东大叔', value: 'guangdongdashu' },
          { label: '广西表哥', value: 'guangxibiaoge' },
          { label: '京腔大哥', value: 'jingqiangdage' },
          { label: '湾湾青年', value: 'wanwanqingnian' },
        ],
      },
    ],
  },
  {
    label: '英语',
    value: 'english',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '饱满激昂', value: 'baomanjiang' },
          { label: '积极活力', value: 'jijihuoli' },
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '饱满激昂', value: 'baomanjiang' },
          { label: '积极活力', value: 'jijihuoli' },
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '日语',
    value: 'japanese',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '积极活力', value: 'jijihuoli' },
          { label: '温柔亲切', value: 'wenrouqinqie' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '稳重深沉', value: 'wenzhongshenchen' },
          { label: '正式官方', value: 'zhengshiguanfang' },
        ],
      },
    ],
  },
  {
    label: '俄语',
    value: 'russian',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '积极活力', value: 'jijihuoli' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '积极活力', value: 'jijihuoli' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
        ],
      },
    ],
  },
  {
    label: '西班牙语',
    value: 'spanish',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '稳重深沉', value: 'wenzhongshenchen' },
        ],
      },
    ],
  },
  {
    label: '韩语',
    value: 'korean',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '德语',
    value: 'german',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '法语',
    value: 'french',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '正式官方', value: 'zhengshiguanfang' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '积极活力', value: 'jijihuoli' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '阿拉伯语',
    value: 'arabic',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
      {
        label: '男性',
        value: 'male',
        styles: [
          { label: '温柔亲切', value: 'wenrouqinqie' },
          { label: '正式官方', value: 'zhengshiguanfang' },
          { label: '自然平和', value: 'ziranpinghe' },
        ],
      },
    ],
  },
  {
    label: '意大利语',
    value: 'italian',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '温柔亲切', value: 'wenrouqinqie' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '温柔亲切', value: 'wenrouqinqie' }],
      },
    ],
  },
  {
    label: '泰语',
    value: 'thai',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '积极活力', value: 'jijihuoli' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '积极活力', value: 'jijihuoli' }],
      },
    ],
  },
  {
    label: '越南语',
    value: 'vietnamese',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '正式官方', value: 'zhengshiguanfang' }],
      },
    ],
  },
  {
    label: '葡萄牙语',
    value: 'portuguese',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '稳重深沉', value: 'wenzhongshenchen' }],
      },
    ],
  },
  {
    label: '马来语',
    value: 'malay',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '积极活力', value: 'jijihuoli' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '积极活力', value: 'jijihuoli' }],
      },
    ],
  },
  {
    label: '印尼语',
    value: 'indonesian',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '甜美可爱', value: 'tianmeikeai' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '正式官方', value: 'zhengshiguanfang' }],
      },
    ],
  },
  {
    label: '荷兰语',
    value: 'dutch',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
    ],
  },
  {
    label: '高棉语',
    value: 'khmer',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '正式官方', value: 'zhengshiguanfang' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '稳重深沉', value: 'wenzhongshenchen' }],
      },
    ],
  },
  {
    label: '老挝语',
    value: 'lao',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '稳重深沉', value: 'wenzhongshenchen' }],
      },
    ],
  },
  {
    label: '泰米尔语',
    value: 'tamil',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '积极活力', value: 'jijihuoli' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
    ],
  },
  {
    label: '菲律宾语',
    value: 'filipino',
    genders: [
      {
        label: '女性',
        value: 'female',
        styles: [{ label: '自然平和', value: 'ziranpinghe' }],
      },
      {
        label: '男性',
        value: 'male',
        styles: [{ label: '温柔亲切', value: 'wenrouqinqie' }],
      },
    ],
  },
];

// ==================== 响应式状态 ====================
const texts: SideMenuPlayerTexts = {
  tag: '立即试听，电影级语音效果',
  languageTitle: '选择语言',
  genderTitle: '选择性别',
  styleTitle: '选择风格',
  filtersAria: '视频筛选选项',
  contentAria: '视频播放区域',
  playAria: '播放',
  pauseAria: '暂停',
  unsupportedText: '您的浏览器不支持视频播放，请升级浏览器或使用其他浏览器。',
  emptyTitle: '示例视频',
  emptyDescription: '请选择筛选条件查看配音示例视频',
};

const buildDefaultPosterUrl = (selection: Selection) => {
  return getFeUrl(
    `/rendora/ai-voice/demo/${selection.language}/${selection.gender}/${selection.style}.png`,
  );
};

const buildVideoUrl = (selection: Selection) => {
  return getFeUrl(
    `/youyan-platform/ai-voice/ai-voice-demo/${selection.language}/${selection.gender}/${selection.style}.webm`,
  );
};

const buildVideoPosterUrl = (selection: Selection) => {
  return getFeUrl(
    `/youyan-platform/ai-voice/ai-voice-demo/${selection.gender}.png?x-oss-process=image/format,webp`,
  );
};

const buildVideoTitle = (labels: SelectionLabels) => {
  return `${labels.language}${labels.gender}${labels.style}风格配音示例`;
};

const buildVideoDescription = (labels: SelectionLabels) => {
  return `这是一段${labels.language}语言，${labels.gender}声音，${labels.style}风格的配音示例视频。`;
};

const playerRef = ref<InstanceType<typeof SideMenuPlayerWidget> | null>(null);

const play = () => {
  playerRef.value?.play();
};

const pause = () => {
  playerRef.value?.pause();
};

const seek = (time: number) => {
  playerRef.value?.seek(time);
};

const videoRef = computed(() => playerRef.value?.videoRef);

defineExpose({
  play,
  pause,
  seek,
  videoRef,
});
</script>
